// Directory monitoring.
//
// Primary mechanism is FileSystemObserver, which notifies on actual file system
// changes. It is still experimental, so support is probed at runtime and any
// failure degrades to polling instead of breaking monitoring.
//
// Polling only reads directory metadata (name, size, lastModified). File content
// is never re-read to detect a change; that is what the stored size/mtime pair
// is for.

import { scanDirectory, supportsFileSystemObserver } from './directory'

export const VISIBLE_POLL_MS = 2000
export const HIDDEN_POLL_MS = 15000
const OBSERVER_RECONCILE_MS = 30000
const OBSERVER_DEBOUNCE_MS = 400

export function createMonitor(options = {}) {
    const onScan = options.onScan || (() => {})
    const onError = options.onError || (() => {})
    const onState = options.onState || (() => {})

    const state = {
        mode: 'idle',
        paused: false,
        running: false,
        lastScanAt: 0,
        lastScanReason: '',
        intervalMs: VISIBLE_POLL_MS,
        observerActive: false,
        scanning: false,
        error: '',
    }

    let handle = null
    let observer = null
    let pollTimer = null
    let debounceTimer = null
    let reconcileTimer = null
    let scanQueued = false
    let disposed = false

    function publish() {
        onState({ ...state })
    }

    function isHidden() {
        return typeof document !== 'undefined' && document.visibilityState === 'hidden'
    }

    async function runScan(reason) {
        if (disposed || !handle || state.paused) return null
        if (state.scanning) {
            scanQueued = true
            return null
        }

        state.scanning = true
        publish()

        try {
            const result = await scanDirectory(handle)
            state.lastScanAt = result.scannedAt
            state.lastScanReason = reason
            state.error = ''
            await onScan({ ...result, reason })
            return result
        } catch (error) {
            state.error = error && error.message ? error.message : String(error)
            onError(error, reason)
            return null
        } finally {
            state.scanning = false
            publish()
            if (scanQueued) {
                scanQueued = false
                // A change arrived mid-scan; pick it up on the next tick.
                window.setTimeout(() => runScan('queued'), 0)
            }
        }
    }

    function clearPolling() {
        if (pollTimer) {
            window.clearInterval(pollTimer)
            pollTimer = null
        }
    }

    function startPolling() {
        clearPolling()
        state.intervalMs = isHidden() ? HIDDEN_POLL_MS : VISIBLE_POLL_MS
        pollTimer = window.setInterval(() => runScan('poll'), state.intervalMs)
    }

    function startReconcile() {
        if (reconcileTimer) window.clearInterval(reconcileTimer)
        // Even with an observer attached, a periodic sweep catches changes the
        // experimental API misses (and changes made while the tab was closed).
        reconcileTimer = window.setInterval(() => runScan('reconcile'), OBSERVER_RECONCILE_MS)
    }

    async function attachObserver() {
        if (!supportsFileSystemObserver()) return false
        try {
            observer = new window.FileSystemObserver(() => {
                if (debounceTimer) window.clearTimeout(debounceTimer)
                debounceTimer = window.setTimeout(() => runScan('observer'), OBSERVER_DEBOUNCE_MS)
            })
            await observer.observe(handle, { recursive: false })
            state.observerActive = true
            return true
        } catch (error) {
            observer = null
            state.observerActive = false
            return false
        }
    }

    function detachObserver() {
        if (observer) {
            try {
                observer.disconnect()
            } catch (error) {
                // Disconnecting a dead observer is not an error worth surfacing.
            }
        }
        observer = null
        state.observerActive = false
    }

    function handleVisibility() {
        if (!state.running || state.paused) return
        if (state.mode === 'polling') startPolling()
        if (!isHidden()) runScan('visible')
    }

    function handleFocus() {
        if (!state.running || state.paused) return
        runScan('focus')
    }

    return {
        get state() {
            return { ...state }
        },

        async start(directoryHandle) {
            handle = directoryHandle
            disposed = false
            state.running = true
            state.paused = false

            const observed = await attachObserver()
            state.mode = observed ? 'observer' : 'polling'
            if (!observed) startPolling()
            startReconcile()

            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', handleVisibility)
                window.addEventListener('focus', handleFocus)
            }

            publish()
            await runScan('start')
        },

        pause() {
            state.paused = true
            clearPolling()
            detachObserver()
            publish()
        },

        async resume() {
            if (!handle) return
            state.paused = false
            const observed = await attachObserver()
            state.mode = observed ? 'observer' : 'polling'
            if (!observed) startPolling()
            publish()
            await runScan('resume')
        },

        scanNow(reason = 'manual') {
            return runScan(reason)
        },

        stop() {
            disposed = true
            state.running = false
            state.mode = 'idle'
            clearPolling()
            detachObserver()
            if (reconcileTimer) {
                window.clearInterval(reconcileTimer)
                reconcileTimer = null
            }
            if (debounceTimer) {
                window.clearTimeout(debounceTimer)
                debounceTimer = null
            }
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibility)
                window.removeEventListener('focus', handleFocus)
            }
            handle = null
            publish()
        },
    }
}
